/**
 * Reusable Form Error Alert Component
 * Displays API errors inline on form pages
 */

import React from "react";
import { Alert, AlertTitle, Collapse } from "@mui/material";
import { useEffect, useRef } from "react";

interface FormErrorAlertProps {
  error: string | null;
  onClose?: () => void;
  title?: string;
}

/**
 * Extract error message from various error formats
 */
export const getErrorMessage = (error: unknown): string => {
  if (!error) return "An unexpected error occurred";

  // Handle Axios error response
  if (typeof error === "object" && error !== null) {
    const err = error as Record<string, unknown>;

    // Check for Axios error structure
    if (err.response && typeof err.response === "object") {
      const response = err.response as Record<string, unknown>;
      if (response.data && typeof response.data === "object") {
        const data = response.data as Record<string, unknown>;

        // Try error.message first (our API format)
        if (data.error && typeof data.error === "object") {
          const errorObj = data.error as Record<string, unknown>;
          if (typeof errorObj.message === "string") {
            return errorObj.message;
          }
        }

        // Try message directly
        if (typeof data.message === "string") {
          return data.message;
        }
      }
    }

    // Handle Error object
    if (err.message && typeof err.message === "string") {
      return err.message;
    }
  }

  // Handle string error
  if (typeof error === "string") {
    return error;
  }

  return "An unexpected error occurred";
};

/**
 * FormErrorAlert - Displays error messages in forms
 */
export const FormErrorAlert: React.FC<FormErrorAlertProps> = ({
  error,
  onClose,
  title = "Error",
}) => {
  const alertRef = useRef<HTMLDivElement>(null);

  // Scroll the error into view when it appears so a submit failure isn't missed
  // above the fold on a long form. (Alert's role="alert" handles SR announce.)
  useEffect(() => {
    if (error) {
      alertRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [error]);

  return (
    <Collapse in={!!error}>
      <Alert
        ref={alertRef}
        severity="error"
        role="alert"
        onClose={onClose}
        sx={{ mb: 3 }}
      >
        {title !== "Error" && <AlertTitle>{title}</AlertTitle>}
        {error}
      </Alert>
    </Collapse>
  );
};

export default FormErrorAlert;
